import { BedDouble, Building2, CalendarDays, Check, ChevronLeft, ChevronRight, Layers3, MapPin, Phone, Ruler, Share2, Trash2, UserRound } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as InstagramIcon } from "../../assets/icons/social/instagram.svg";
import { ReactComponent as TelegramIcon } from "../../assets/icons/social/telegram.svg";
import { ReactComponent as WhatsappIcon } from "../../assets/icons/social/whatsapp.svg";
import { API_ENDPOINTS } from "../../constants/common";
import { DefaultAvatar } from "../../components/common/DefaultAvatar";
import { Error } from "../../components/common/Error";
import { PageNavigation } from "../../components/common/PageNavigation";
import { apiClient, formatInternationalPhone, getPhoneHref } from "../../utils";
import { RootState } from "../../store";
import { selectUserData, selectUserInfo, selectUserToken } from "../../store/selectors";
import { loadYandexMaps, parseCoordinate, YandexMapInstance } from "../../utils/yandexMaps";
import { resolveListingTotalPrice } from "../../utils/listingPrice";
import {
  ANALYTICS_EVENTS,
  getMarketListingAnalyticsProperties,
  trackAnalyticsEvent,
} from "../../analytics";

type ListingType = "house" | "land_plot" | "apartment" | "commercial";
type ModerationStatus = "pending" | "approved" | "rejected";
type RentTerm = "short_term" | "long_term";

const PUBLISHED_STATUS_ID = 1;
const UNPUBLISHED_STATUS_ID = 2;

type ListingImage = {
  id: number | string;
  url: string;
  is_primary: boolean;
  sort_order: number;
};

type PaymentTerm = {
  period: string;
  price: number | string;
  currency_id?: number | string;
};

type ListingDetail = {
  id: number | string;
  type: ListingType;
  owner_id: number | string;
  author_id?: number | string;
  title: string;
  market_type: "sale" | "rent";
  board_category: string;
  board_subcategory?: string | null;
  status_id: number | string;
  moderation_status?: ModerationStatus;
  moderation_reason?: string | null;
  moderation_submitted_at?: string | null;
  moderated_at?: string | null;
  longitude: number | string;
  latitude: number | string;
  image_url?: string | null;
  description?: string | null;
  images?: ListingImage[];
  addons?: Record<string, unknown>;
  payment_terms?: PaymentTerm[];
  assignment?: Record<string, unknown> | null;
  rent_term?: RentTerm | null;
  price_sqm?: number | string | null;
  price_total?: number | string | null;
  price_monthly?: number | string | null;
  created_at: string;
  updated_at: string;
  room_count?: number | string;
  floor_count?: number | string | null;
  floor?: number | string;
  area_sqm?: number | string;
  area_site?: number | string;
  type_id?: number | string;
  purpose_id?: number | string;
  window_view?: string;
  construction_year?: number | string | null;
  completion_year?: number | string | null;
};

type UserNode = {
  id?: number | string;
  user_id?: number | string;
  userId?: number | string;
  abilities?: string[];
  token?: string | null;
  data?: UserNode;
  profile?: UserNode;
  user?: UserNode;
} | null;
type UserInfo = UserNode;
type UserDataShape = UserNode;
type AuthorProfile = Record<string, any> | null;
type ShareState = "idle" | "pending" | "copied";

type Props = {
  user: UserInfo;
  userData: UserDataShape;
  userToken?: string | null;
};

type ListingDescriptionProps = {
  description?: string | null;
  className?: string;
};

const ListingDescription: React.FC<ListingDescriptionProps> = ({
  description,
  className = "",
}) => {
  const textRef = React.useRef<HTMLParagraphElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const text = description || "Описание пока не добавлено.";

  useEffect(() => {
    setIsExpanded(false);
  }, [description]);

  useEffect(() => {
    const textNode = textRef.current;
    if (!textNode || isExpanded) return;

    const checkOverflow = () => {
      const hasOverflow = textNode.scrollHeight > textNode.clientHeight + 1;
      setCanExpand((currentValue) =>
        currentValue === hasOverflow ? currentValue : hasOverflow,
      );
    };

    checkOverflow();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(textNode);
    return () => observer.disconnect();
  }, [isExpanded, text]);

  return (
    <section className={className}>
      <h2 className="m-0 text-xl font-bold text-[#1a1a1a]">Описание</h2>
      <p
        ref={textRef}
        className={`mt-3 whitespace-pre-line [overflow-wrap:anywhere] text-[15px] leading-7 text-[#4f4f4f] ${
          isExpanded ? "" : "line-clamp-6"
        }`}
      >
        {text}
      </p>
      {canExpand ? (
        <button
          type="button"
          className="mt-3 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold text-[#a12d68] transition hover:text-[#c8508a]"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((currentValue) => !currentValue)}
        >
          {isExpanded ? "Свернуть" : "Читать полностью"}
        </button>
      ) : null}
    </section>
  );
};

const SITE_TITLE = "Husam Helper";
const UNAVAILABLE_LISTING_TITLE = `Объявление недоступно — ${SITE_TITLE}`;

const TYPE_LABELS: Record<ListingType, string> = {
  house: "Дом",
  land_plot: "Земельный участок",
  apartment: "Квартира",
  commercial: "Коммерческая недвижимость",
};

const CATEGORY_LABELS: Record<string, string> = {
  houses: "Дома",
  land_plots: "Земельные участки",
  assignments: "Переуступки",
  secondary: "Вторичное жильё",
  apartments: "Квартиры",
  commercial: "Коммерция",
  dnt: "ДНТ",
  snt: "СНТ",
  izhs: "ИЖС",
  parking: "Паркинг",
};

const MODERATION_LABELS: Record<ModerationStatus, string> = {
  pending: "На модерации",
  approved: "Модерация пройдена",
  rejected: "Отклонено",
};

const ADDON_LABELS: Record<string, string> = {
  is_renovated: "Ремонт",
  is_penthouse: "Пентхаус",
  is_apartment_unit: "Апартаменты",
  is_view: "Видовой объект",
  house_kind: "Тип дома",
  is_completed: "Объект сдан",
  has_pool: "Бассейн",
  has_bathhouse: "Баня",
  has_sewerage: "Канализация",
  has_water: "Водоснабжение",
  has_electricity: "Электричество",
  has_gas: "Газ",
  address_region: "Регион",
  address_locality: "Населённый пункт",
  address_street: "Улица",
  address_house: "Дом",
  utility_included: "Коммунальные платежи включены",
  utilities_payer: "Коммунальные платит",
  rent_term: "Срок аренды",
  agent_commission_percent: "Комиссия агента",
  agent_commission_amount: "Комиссия агента",
  assignment_months_left: "Осталось месяцев",
  assignment_paid_amount: "Оплаченная сумма",
  assignment_paid_months: "Оплачено месяцев",
};

const ASSIGNMENT_LABELS: Record<string, string> = {
  initial_payment: "Первоначальный взнос",
  payment: "Ежемесячный платёж",
  full_payment_purchase: "Полная стоимость покупки",
  full_payment_price: "Стоимость по договору",
  agent_markup: "Агентская наценка",
  recalculation_percentage: "Процент перерасчёта",
  recalculation_period: "Период перерасчёта",
  initial_payment_at: "Дата первого платежа",
  assignment_months_left: "Осталось месяцев",
  assignment_paid_amount: "Оплаченная сумма",
  assignment_paid_months: "Оплачено месяцев",
};

const ASSIGNMENT_HIDDEN_FIELDS = new Set(["id", "enabled", "currency_id", "created_at", "updated_at"]);
const ASSIGNMENT_MONEY_FIELDS = new Set([
  "initial_payment",
  "payment",
  "full_payment_purchase",
  "full_payment_price",
  "agent_markup",
  "assignment_paid_amount",
]);

const HOUSE_KIND_LABELS: Record<string, string> = {
  house: "Дом",
  dacha: "Дача",
  cottage: "Коттедж",
  townhouse: "Таунхаус",
};

const UTILITIES_PAYER_LABELS: Record<string, string> = {
  tenant: "Арендатор",
  landlord: "Собственник",
};

const RENT_TERM_LABELS: Record<RentTerm, string> = {
  short_term: "1-3 месяца",
  long_term: "Долгосрок, более 3 месяцев",
};

const unwrapValue = (value: unknown): unknown => {
  if (value && typeof value === "object" && "value" in value) {
    return (value as { value: unknown }).value;
  }
  return value;
};

const formatValue = (value: unknown): string => {
  const unwrapped = unwrapValue(value);
  if (typeof unwrapped === "boolean") return unwrapped ? "Да" : "Нет";
  if (unwrapped === null || unwrapped === undefined || unwrapped === "") return "—";
  return String(unwrapped);
};

const formatDecimal = (value: unknown, maximumFractionDigits = 2): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits }).format(parsed);
};

const formatAddonValue = (key: string, value: unknown): string => {
  const unwrapped = unwrapValue(value);
  if (key === "house_kind" && typeof unwrapped === "string") {
    return HOUSE_KIND_LABELS[unwrapped] || unwrapped;
  }
  if (key === "utilities_payer" && typeof unwrapped === "string") {
    return UTILITIES_PAYER_LABELS[unwrapped] || unwrapped;
  }
  if (key === "rent_term" && (unwrapped === "short_term" || unwrapped === "long_term")) {
    return RENT_TERM_LABELS[unwrapped];
  }
  if (key === "agent_commission_percent") {
    const formatted = formatDecimal(unwrapped);
    return formatted && Number(unwrapped) > 0 ? `${formatted}%` : formatValue(value);
  }
  if (key === "agent_commission_amount" || key === "assignment_paid_amount") {
    return formatMoney(unwrapped) || formatValue(value);
  }
  return formatValue(value);
};

const formatMoney = (value: unknown): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(parsed)} ₽`;
};

const formatAssignmentValue = (key: string, value: unknown): string => {
  if (key.endsWith("_at")) return formatDate(value);
  if (ASSIGNMENT_MONEY_FIELDS.has(key)) return formatMoney(unwrapValue(value)) || formatValue(value);
  if (key === "recalculation_percentage") {
    const formatted = formatDecimal(unwrapValue(value));
    return formatted ? `${formatted}%` : formatValue(value);
  }
  return formatValue(value);
};

const formatAgentCommission = (percentValue: unknown, amountValue: unknown): string => {
  const commissionPercent = Number(unwrapValue(percentValue));
  const formattedCommissionPercent = formatDecimal(commissionPercent);
  const commissionAmount = formatMoney(unwrapValue(amountValue));
  const commissionParts = [
    formattedCommissionPercent && commissionPercent > 0 ? `${formattedCommissionPercent}%` : null,
    commissionAmount,
  ].filter(Boolean);
  return commissionParts.length ? commissionParts.join(" / ") : "Договорная";
};

const formatUtilitiesPayer = (payerValue: unknown, legacyUtilityIncluded: unknown): string => {
  const payer = unwrapValue(payerValue);
  if (typeof payer === "string" && UTILITIES_PAYER_LABELS[payer]) {
    return UTILITIES_PAYER_LABELS[payer];
  }

  const legacy = unwrapValue(legacyUtilityIncluded);
  if (legacy === true || legacy === "true" || legacy === 1 || legacy === "1") {
    return UTILITIES_PAYER_LABELS.landlord;
  }
  if (legacy === false || legacy === "false" || legacy === 0 || legacy === "0") {
    return UTILITIES_PAYER_LABELS.tenant;
  }

  return "Договорная";
};

const formatRentTerm = (value: unknown): string => {
  const rentTerm = unwrapValue(value);
  return rentTerm === "short_term" || rentTerm === "long_term" ? RENT_TERM_LABELS[rentTerm] : "Договорная";
};

const formatSiteArea = (value: unknown): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const sotkaValue = parsed / 100;
  const formatted = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 2,
  }).format(sotkaValue);
  return `${formatted} сот.`;
};

const formatDate = (value: unknown): string => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeAuthorId = (value: unknown): string => {
  const raw = getString(value);
  if (!raw || raw === "undefined" || raw === "null") return "";
  return raw;
};

const getAuthorName = (author: AuthorProfile, authorId: string): string => {
  const candidates = [
    [author?.firstname, author?.lastname].filter(Boolean).join(" "),
    [author?.first_name, author?.last_name].filter(Boolean).join(" "),
    [author?.firstName, author?.lastName].filter(Boolean).join(" "),
    [author?.name, author?.surname].filter(Boolean).join(" "),
    author?.name,
    author?.full_name,
    author?.fullName,
    author?.email,
    author?.phone,
  ];

  for (const candidate of candidates) {
    const value = getString(candidate);
    if (value) return value;
  }

  return authorId ? `Пользователь #${authorId}` : "Автор объявления";
};

const getAuthorAvatar = (author: AuthorProfile): string =>
  getString(author?.avatar_url || author?.avatarUrl || author?.avatar || author?.photo_url || author?.image_url);

const getAuthorContact = (author: AuthorProfile, key: string): string => {
  const direct = getString(author?.[key]);
  if (direct) return direct;

  const socials = Array.isArray(author?.socials) ? author.socials : [];
  const social = socials.find(
    (item: any) => item?.type === key || item?.code === key || item?.name === key,
  );
  return getString(social?.value);
};

const getSocialUrl = (type: "telegram" | "whatsapp" | "instagram", value: string): string => {
  const normalizedValue = value.trim();
  if (/^https?:\/\//i.test(normalizedValue)) return normalizedValue;

  if (type === "whatsapp") {
    const internationalPhone = formatInternationalPhone(normalizedValue)?.replace(/\D/g, "") || "";
    return `https://wa.me/${internationalPhone}`;
  }

  const username = normalizedValue.replace(/^@/, "");
  return type === "telegram"
    ? `https://t.me/${username}`
    : `https://instagram.com/${username}`;
};

const isClassifiedsListPath = (value: string): boolean =>
  /^\/(?:classifieds(?:\/(?:my|favorites)|\/users\/[^/?#]+)?|control-panel\/users\/[^/?#]+)(?:\?.*)?$/.test(value);

const isValidAuthToken = (value: unknown): boolean => {
  const token = getString(value);
  return Boolean(token && token !== "undefined" && token !== "null");
};

const hasAuthToken = (userToken?: string | null): boolean => {
  if (isValidAuthToken(userToken)) return true;

  const cookieToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("userToken="))
    ?.slice("userToken=".length);

  if (!cookieToken) return false;

  try {
    return isValidAuthToken(decodeURIComponent(cookieToken));
  } catch {
    return isValidAuthToken(cookieToken);
  }
};

const copyText = async (value: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) throw new Error("Copy command failed");
};

const hasUserAbility = (ability: string, ...nodes: UserNode[]): boolean => {
  const stack = nodes.filter(Boolean) as NonNullable<UserNode>[];
  const visited = new Set<NonNullable<UserNode>>();

  while (stack.length) {
    const node = stack.pop();
    if (!node || visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node.abilities) && node.abilities.includes(ability)) {
      return true;
    }

    if (node.data) stack.push(node.data);
    if (node.profile) stack.push(node.profile);
    if (node.user) stack.push(node.user);
  }

  return false;
};

const resolveUserId = (...nodes: UserNode[]): string => {
  const stack = nodes.filter(Boolean) as NonNullable<UserNode>[];
  const visited = new Set<NonNullable<UserNode>>();

  while (stack.length) {
    const node = stack.pop();
    if (!node || visited.has(node)) continue;
    visited.add(node);

    for (const candidate of [node.id, node.user_id, node.userId]) {
      const normalizedId = normalizeAuthorId(candidate);
      if (normalizedId) return normalizedId;
    }

    if (node.data) stack.push(node.data);
    if (node.profile) stack.push(node.profile);
    if (node.user) stack.push(node.user);
  }

  return "";
};

type LocationMapProps = {
  latitude: number;
  longitude: number;
  title: string;
  address?: string;
};

const ClassifiedLocationMap: React.FC<LocationMapProps> = ({ latitude, longitude, title, address }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<YandexMapInstance | null>(null);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");
  const coordinates = useMemo<[number, number]>(() => [latitude, longitude], [latitude, longitude]);

  useEffect(() => {
    let cancelled = false;
    setMapStatus("loading");

    loadYandexMaps()
      .then((ymaps) => {
        if (cancelled || !containerRef.current) return;

        mapRef.current?.destroy();
        const map = new ymaps.Map(
          containerRef.current,
          {
            center: coordinates,
            zoom: 15,
            controls: ["zoomControl", "fullscreenControl"],
          },
          {
            suppressMapOpenBlock: true,
            yandexMapDisablePoiInteractivity: true,
          },
        );
        const placemark = new ymaps.Placemark(
          coordinates,
          {
            hintContent: title,
            balloonContentHeader: title,
            balloonContentBody:
              address || `${formatDecimal(latitude, 7)}; ${formatDecimal(longitude, 7)}`,
          },
          {
            preset: "islands#redHomeIcon",
          },
        );

        map.geoObjects.add(placemark);
        mapRef.current = map;
        setMapStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setMapStatus("error");
      });

    return () => {
      cancelled = true;
      mapRef.current?.destroy();
      mapRef.current = null;
    };
  }, [address, coordinates, latitude, longitude, title]);

  return (
    <section className="overflow-hidden rounded-2xl border border-[#e8e8e8] bg-white" aria-label="Местоположение">
      <div className="flex items-start gap-2 border-b border-[#ececec] px-4 py-3">
        <MapPin className="mt-0.5 shrink-0 text-[#c8508a]" size={18} />
        <div>
          <h2 className="m-0 text-lg font-bold text-[#1a1a1a]">Местоположение</h2>
          {address ? <p className="m-0 mt-1 text-sm leading-5 text-[#6b6b6b]">{address}</p> : null}
        </div>
      </div>

      <div className="relative h-[320px] bg-[#f2f2f2]">
        <div ref={containerRef} className="h-full w-full" />

        {mapStatus === "loading" ? (
          <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] text-sm text-[#777]">
            Загружаем карту...
          </div>
        ) : null}

        {mapStatus === "error" ? (
          <div className="absolute inset-0 grid place-items-center bg-[#f7f7f7] px-5 text-center text-sm leading-6 text-[#777]">
            Карту не удалось загрузить. Попробуйте обновить страницу.
          </div>
        ) : null}
      </div>
    </section>
  );
};

const _ClassifiedListingDetail: React.FC<Props> = ({ user, userData, userToken }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, id, token } = useParams();
  const isSharedListing = Boolean(token);
  const isAuthenticated = hasAuthToken(userToken);
  const navigationState = location.state as {
    classifiedsReturnTo?: string;
  } | null;
  const hasClassifiedsReturnTarget =
    typeof navigationState?.classifiedsReturnTo === "string" &&
    isClassifiedsListPath(navigationState.classifiedsReturnTo);
  const classifiedsReturnTo =
    hasClassifiedsReturnTarget ? navigationState.classifiedsReturnTo as string : "/classifieds";
  const [listing, setListing] = useState<ListingDetail | null>(null);
  const [sharedByUserId, setSharedByUserId] = useState<number | string | null>(null);
  const [author, setAuthor] = useState<AuthorProfile>(null);
  const [authorPending, setAuthorPending] = useState(false);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [shareState, setShareState] = useState<ShareState>("idle");
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [publicationPending, setPublicationPending] = useState(false);
  const [publicationError, setPublicationError] = useState<string | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const imageTouchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const trackedListingViewRef = React.useRef("");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [id, token, type]);

  const loadListing = useCallback(async () => {
    if ((!isSharedListing && (!type || !id)) || (isSharedListing && !token)) {
      setError("Некорректная ссылка на объявление");
      setPending(false);
      return;
    }
    setPending(true);
    setError(null);
    try {
      if (token) {
        const response = await apiClient.get(API_ENDPOINTS.classifiedsSharedListing(token));
        const sharedListing = response?.data?.data;
        setListing(sharedListing?.listing || null);
        setSharedByUserId(sharedListing?.shared_by_user_id ?? null);
      } else if (type && id) {
        const response = await apiClient.get(API_ENDPOINTS.classifiedsListing(type, id));
        setListing(response?.data?.data || null);
        setSharedByUserId(null);
      }
      setActiveImageIndex(0);
    } catch (requestError: any) {
      const status = requestError?.response?.status;
      setError(
        status === 404
          ? "Объявление не найдено или недоступно"
          : requestError?.response?.data?.error || "Не удалось загрузить объявление",
      );
    } finally {
      setPending(false);
    }
  }, [id, isSharedListing, token, type]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  useEffect(() => {
    if (pending || error || !listing) {
      return;
    }

    const trackingKey = `${isSharedListing ? "shared" : "direct"}:${listing.type}:${listing.id}:${token || ""}`;
    if (trackedListingViewRef.current === trackingKey) {
      return;
    }
    trackedListingViewRef.current = trackingKey;

    trackAnalyticsEvent(
      isSharedListing
        ? ANALYTICS_EVENTS.MARKET_SHARED_LISTING_OPENED
        : ANALYTICS_EVENTS.MARKET_LISTING_VIEWED,
      {
        ...getMarketListingAnalyticsProperties(listing),
        share_token: token,
        attributed_agent_user_id: sharedByUserId || undefined,
      },
    );
  }, [
    error,
    isSharedListing,
    listing,
    pending,
    sharedByUserId,
    token,
  ]);

  useEffect(() => {
    if (pending) return;

    document.title =
      listing && !error
        ? getString(listing.title) || SITE_TITLE
        : UNAVAILABLE_LISTING_TITLE;

    return () => {
      document.title = SITE_TITLE;
    };
  }, [error, listing, pending]);

  useEffect(() => {
    setShareUrl(null);
    setShareState("idle");
    setShareError(null);
  }, [id, isAuthenticated, token, type]);

  const profileAuthorId = normalizeAuthorId(sharedByUserId ?? listing?.author_id ?? listing?.owner_id);

  useEffect(() => {
    const authorId = profileAuthorId;
    if (!authorId) {
      setAuthor(null);
      setAuthorPending(false);
      return;
    }

    let cancelled = false;
    const loadAuthor = async () => {
      setAuthorPending(true);
      try {
        const response = await apiClient.get(`${API_ENDPOINTS.getUserInfoById}${authorId}`);
        if (!cancelled) {
          const profile = response?.data?.data ?? response?.data;
          setAuthor(profile && typeof profile === "object" ? profile : null);
        }
      } catch {
        if (!cancelled) setAuthor(null);
      } finally {
        if (!cancelled) setAuthorPending(false);
      }
    };

    loadAuthor();

    return () => {
      cancelled = true;
    };
  }, [profileAuthorId]);

  useEffect(() => {
    if (shareState !== "copied") return;
    const timeoutId = window.setTimeout(() => setShareState("idle"), 2500);
    return () => window.clearTimeout(timeoutId);
  }, [shareState]);

  const gallery = useMemo(() => {
    if (!listing) return [];
    const images = Array.isArray(listing.images) ? [...listing.images].sort((a, b) => a.sort_order - b.sort_order) : [];
    if (!images.length && listing.image_url) {
      return [
        {
          id: "primary",
          url: listing.image_url,
          is_primary: true,
          sort_order: 0,
        },
      ];
    }
    return images;
  }, [listing]);

  const facts = useMemo(() => {
    if (!listing) return [];
    const isAssignment = listing.board_category === "assignments";
    const assignmentHouseCompleted = isAssignment && Boolean(listing.construction_year);
    const applicableYear = isAssignment
      ? listing.construction_year || listing.completion_year
      : listing.construction_year;
    const siteArea = formatSiteArea(listing.area_site);

    return [
      listing.area_sqm
        ? { icon: Ruler, label: "Площадь", value: `${formatDecimal(listing.area_sqm, 1)} м²` }
        : null,
      siteArea ? { icon: Ruler, label: "Участок", value: siteArea } : null,
      listing.room_count
        ? {
            icon: BedDouble,
            label: "Комнаты",
            value: String(listing.room_count),
          }
        : null,
      listing.floor
        ? {
            icon: Layers3,
            label: "Этаж",
            value: listing.floor_count ? `${listing.floor} из ${listing.floor_count}` : String(listing.floor),
          }
        : listing.floor_count
          ? {
              icon: Building2,
              label: "Этажей",
              value: String(listing.floor_count),
            }
          : null,
      applicableYear
        ? {
            icon: CalendarDays,
            label: isAssignment && !assignmentHouseCompleted ? "Год сдачи" : "Год постройки",
            value: String(applicableYear),
          }
        : null,
    ].filter(Boolean) as Array<{
      icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
      label: string;
      value: string;
    }>;
  }, [listing]);

  const shareListing = useCallback(async () => {
    if (!listing || shareState === "pending") return;

    setShareError(null);

    const shareResolvedUrl = async (url: string, copyErrorMessage: string) => {
      let copied = false;

      try {
        await copyText(url);
        copied = true;
        setShareState("copied");
      } catch {
        setShareError(copyErrorMessage);
        setShareState("idle");
      }

      if (!navigator.share) return;

      try {
        await navigator.share({
          title: listing.title,
          text: "Посмотрите это объявление",
          url,
        });
      } catch (shareRequestError: any) {
        if (shareRequestError?.name !== "AbortError" && !copied) {
          setShareError("Не удалось поделиться ссылкой. Попробуйте ещё раз");
        }
      }
    };

    if (shareUrl) {
      await shareResolvedUrl(shareUrl, "Не удалось скопировать ссылку. Попробуйте ещё раз");
      return;
    }

    if (!isAuthenticated) {
      const url = new URL(
        `/classifieds/${encodeURIComponent(listing.type)}/${encodeURIComponent(String(listing.id))}`,
        window.location.origin,
      ).toString();
      setShareUrl(url);
      trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_SHARED, {
        ...getMarketListingAnalyticsProperties(listing),
        share_mode: "direct_url",
      });
      await shareResolvedUrl(url, "Не удалось скопировать ссылку. Попробуйте ещё раз");
      return;
    }

    setShareState("pending");

    try {
      const response = await apiClient.post(API_ENDPOINTS.classifiedsListingShare(listing.type, listing.id));
      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Unexpected share response status");
      }

      const path = getString(response?.data?.data?.path);
      if (!path) throw new Error("Share path is missing");

      const url = new URL(path, window.location.origin).toString();
      setShareUrl(url);
      trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_SHARED, {
        ...getMarketListingAnalyticsProperties(listing),
        share_mode: "generated_link",
        share_token: new URL(url).pathname.split("/").filter(Boolean).pop(),
      });
      await shareResolvedUrl(url, "Ссылка получена, но не удалось скопировать её");
    } catch (requestError: any) {
      const status = requestError?.response?.status;

      if (status === 401) {
        const url = window.location.href;
        setShareUrl(url);
        trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_SHARED, {
          ...getMarketListingAnalyticsProperties(listing),
          share_mode: "direct_url_auth_fallback",
        });
        await shareResolvedUrl(url, "Не удалось скопировать ссылку. Попробуйте ещё раз");
        return;
      }

      trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_LISTING_SHARE_FAILED, {
        ...getMarketListingAnalyticsProperties(listing),
        http_status: status,
      });
      setShareError(
        status === 404
          ? "Поделиться можно только опубликованным объявлением"
          : status === 429
            ? "Слишком много запросов. Попробуйте позже"
            : requestError?.response?.data?.error || "Не удалось получить ссылку. Попробуйте ещё раз",
      );
      setShareState("idle");
    }
  }, [isAuthenticated, listing, shareState, shareUrl]);

  const toggleListingPublication = useCallback(async () => {
    if (!listing || publicationPending) return;

    const isPublished =
      Number(listing.status_id ?? PUBLISHED_STATUS_ID) === PUBLISHED_STATUS_ID &&
      listing.moderation_status !== "pending" &&
      listing.moderation_status !== "rejected";
    const nextStatusId = isPublished ? UNPUBLISHED_STATUS_ID : PUBLISHED_STATUS_ID;

    setPublicationPending(true);
    setPublicationError(null);
    try {
      const response = await apiClient.patch(API_ENDPOINTS.classifiedsListing(listing.type, listing.id), {
        status_id: nextStatusId,
      });
      const responseStatusId = Number(response?.data?.data?.status_id);
      const resolvedStatusId = Number.isFinite(responseStatusId) ? responseStatusId : nextStatusId;
      setListing((current) => (current ? { ...current, status_id: resolvedStatusId } : current));
    } catch (requestError: any) {
      setPublicationError(requestError?.response?.data?.error || "Не удалось изменить статус публикации");
    } finally {
      setPublicationPending(false);
    }
  }, [listing, publicationPending]);

  const deleteListing = useCallback(async () => {
    if (!listing || deletePending || isSharedListing) return;

    const shouldDelete = window.confirm(`Удалить объявление «${listing.title}»?`);
    if (!shouldDelete) return;

    setDeletePending(true);
    setDeleteError(null);
    try {
      await apiClient.delete(API_ENDPOINTS.classifiedsListing(listing.type, listing.id));
      navigate(`/classifieds/users/${encodeURIComponent(String(listing.owner_id))}`, { replace: true });
    } catch (requestError: any) {
      setDeleteError(requestError?.response?.data?.error || "Не удалось удалить объявление");
    } finally {
      setDeletePending(false);
    }
  }, [deletePending, isSharedListing, listing, navigate]);

  const trackContactClick = (channel: string) => {
    if (!listing) {
      return;
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.MARKET_CONTACT_CLICKED, {
      ...getMarketListingAnalyticsProperties(listing),
      contact_channel: channel,
      contacted_user_id: profileAuthorId || undefined,
      share_token: token,
      attributed_agent_user_id: sharedByUserId || undefined,
    });
  };

  if (pending) {
    return (
      <main className="mx-auto grid max-w-[1344px] gap-5 px-3.5 py-6 md:px-6 lg:px-10 xl:px-12">
        <PageNavigation backLabel="К объявлениям" backTo={classifiedsReturnTo} />
        <div className="grid animate-pulse gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.75fr)]">
          <div className="aspect-[4/3] rounded-2xl bg-[#e8e8e8]" />
          <div className="grid content-start gap-4 pt-2">
            <div className="h-7 w-28 rounded-full bg-[#f1e7ec]" />
            <div className="h-20 rounded-xl bg-[#ececec]" />
            <div className="h-10 w-48 rounded-lg bg-[#eadce4]" />
          </div>
        </div>
      </main>
    );
  }

  if (error || !listing) {
    return (
      <main className="mx-auto grid max-w-[900px] gap-6 px-3.5 py-10 md:px-6">
        <PageNavigation backLabel="К объявлениям" backTo={classifiedsReturnTo} />
        <Error
          title="Объявление недоступно"
          description={error || "Бэк вернул пустой ответ"}
          retryCallback={loadListing}
        />
      </main>
    );
  }

  const monthlyTerm = listing.payment_terms?.find((term) => term.period === "monthly");
  const sqmTerm = listing.payment_terms?.find((term) => term.period === "sqm");
  const monthlyPrice = monthlyTerm?.price ?? listing.price_monthly;
  const sqmPrice = sqmTerm?.price ?? listing.price_sqm;
  const pricingArea = listing.type === "land_plot" ? listing.area_site : listing.area_sqm;
  const totalPrice =
    listing.market_type === "sale"
      ? resolveListingTotalPrice({
          area: pricingArea,
          pricePerSqm: sqmPrice,
          explicitTotalPrice: listing.price_total,
        })
      : null;
  const primaryPrice = listing.market_type === "rent" ? formatMoney(monthlyPrice) : formatMoney(totalPrice);
  const unitPrice = listing.type === "land_plot" ? Number(sqmPrice) * 100 : sqmPrice;
  const unitPriceLabel = listing.type === "land_plot" ? "сотку" : "м²";
  const addressParts = [
    unwrapValue(listing.addons?.address_region),
    unwrapValue(listing.addons?.address_locality),
    unwrapValue(listing.addons?.address_street),
    unwrapValue(listing.addons?.address_house),
  ].filter(Boolean);
  const address = addressParts.join(", ");
  const mapLatitude = parseCoordinate(listing.latitude);
  const mapLongitude = parseCoordinate(listing.longitude);
  const mapCoordinates =
    mapLatitude !== null && mapLongitude !== null
      ? {
          latitude: mapLatitude,
          longitude: mapLongitude,
        }
      : null;
  const rentSummary = [];
  if (listing.market_type === "rent") {
    if (listing.type !== "commercial") {
      rentSummary.push(`Срок аренды: ${formatRentTerm(listing.rent_term ?? listing.addons?.rent_term)}`);
    }

    rentSummary.push(
      `Коммунальные платит: ${formatUtilitiesPayer(listing.addons?.utilities_payer, listing.addons?.utility_included)}`,
    );

    rentSummary.push(
      `Комиссия агента: ${formatAgentCommission(
        listing.addons?.agent_commission_percent,
        listing.addons?.agent_commission_amount,
      )}`,
    );
  }
  const addonEntries = Object.entries(listing.addons || {}).filter(
    ([key, value]) => {
      const unwrapped = unwrapValue(value);
      return (
        !key.startsWith("address_") &&
        key !== "price_per_sqm" &&
        !key.startsWith("agent_commission_") &&
        key !== "utilities_payer" &&
        key !== "rent_term" &&
        key !== "utility_included" &&
        (key !== "house_kind" || listing.type === "house") &&
        unwrapped !== false &&
        unwrapped !== null &&
        unwrapped !== undefined &&
        unwrapped !== ""
      );
    },
  );
  const currentUserId = resolveUserId(userData, user);
  const isListingOwner =
    isAuthenticated && Boolean(currentUserId) && currentUserId === normalizeAuthorId(listing.owner_id);
  const canEdit = hasUserAbility("manager", userData, user) || isListingOwner;
  const canManagePublication = canEdit;
  const canDelete = canEdit && !isSharedListing;
  const isPublished =
    Number(listing.status_id ?? PUBLISHED_STATUS_ID) === PUBLISHED_STATUS_ID &&
    listing.moderation_status !== "pending" &&
    listing.moderation_status !== "rejected";
  const publicationBlocked = Boolean(
    listing.moderation_status && listing.moderation_status !== "approved",
  );
  const authorId = profileAuthorId;
  const authorName = getAuthorName(author, authorId);
  const authorAvatar = getAuthorAvatar(author);
  const authorPhone = getAuthorContact(author, "phone");
  const authorPhoneHref = getPhoneHref(authorPhone);
  const authorTelegram = getAuthorContact(author, "telegram");
  const authorWhatsapp = getAuthorContact(author, "whatsapp");
  const authorInstagram = getAuthorContact(author, "instagram");
  const authorSocials = [
    { type: "telegram" as const, value: authorTelegram, label: "Telegram", Icon: TelegramIcon },
    { type: "whatsapp" as const, value: authorWhatsapp, label: "WhatsApp", Icon: WhatsappIcon },
    { type: "instagram" as const, value: authorInstagram, label: "Instagram", Icon: InstagramIcon },
  ].filter((social) => Boolean(social.value));
  const createdAtLabel = formatDate(listing.created_at);
  const updatedAtLabel = formatDate(listing.updated_at);
  const showUpdatedAt = updatedAtLabel !== "—" && updatedAtLabel !== createdAtLabel;

  const shiftImage = (direction: number) => {
    if (gallery.length < 2) return;
    setActiveImageIndex((current) => (current + direction + gallery.length) % gallery.length);
  };

  const handleImageTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) {
      imageTouchStartRef.current = null;
      return;
    }

    const touch = event.touches[0];
    imageTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleImageTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const touchStart = imageTouchStartRef.current;
    imageTouchStartRef.current = null;
    if (!touchStart || gallery.length < 2) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const isHorizontalSwipe = Math.abs(deltaX) >= 44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

    if (isHorizontalSwipe) {
      shiftImage(deltaX < 0 ? 1 : -1);
    }
  };

  const returnToClassifieds = () => {
    const hasPreviousHistoryEntry = Number(window.history.state?.idx) > 0;
    if (hasClassifiedsReturnTarget && hasPreviousHistoryEntry) {
      navigate(-1);
      return;
    }
    navigate(classifiedsReturnTo);
  };

  return (
    <main className="mx-auto grid max-w-[1344px] gap-6 px-3.5 pb-14 pt-6 md:px-6 lg:px-10 xl:px-12">
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PageNavigation backLabel="К объявлениям" onBack={returnToClassifieds} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] border-[#d8bfd0] bg-white px-3.5 text-[13px] font-medium leading-none text-[#8b2a5e] transition hover:border-[#c8508a] hover:bg-[#fff8fb] disabled:cursor-wait disabled:opacity-60"
              disabled={shareState === "pending"}
              aria-busy={shareState === "pending"}
              onClick={shareListing}
            >
              {shareState === "copied" ? <Check size={16} /> : <Share2 size={16} />}
              {shareState === "pending"
                ? "Создаём ссылку..."
                : shareState === "copied"
                  ? "Ссылка скопирована"
                  : "Поделиться"}
            </button>
            {canEdit ? (
              <button
                type="button"
                className="h-[38px] cursor-pointer rounded-md border-[1.5px] border-[#c8508a] bg-[#c8508a] px-3.5 text-[13px] font-medium leading-none text-white transition hover:border-[#b3407a] hover:bg-[#b3407a]"
                onClick={() => navigate(`/classifieds/${listing.type}/${listing.id}/edit`)}
              >
                Редактировать
              </button>
            ) : null}
            {canManagePublication ? (
              <button
                type="button"
                className={`h-[38px] cursor-pointer rounded-md border-[1.5px] px-3.5 text-[13px] font-medium leading-none transition disabled:cursor-wait disabled:opacity-60 ${
                  isPublished
                    ? "border-[#dedede] bg-white text-[#555] hover:border-[#c8508a] hover:text-[#8b2a5e]"
                    : "border-[#c8508a] bg-[#c8508a] text-white hover:border-[#b3407a] hover:bg-[#b3407a]"
                }`}
                disabled={publicationPending || publicationBlocked}
                aria-busy={publicationPending}
                onClick={toggleListingPublication}
              >
                {publicationBlocked
                  ? "Ждёт решения"
                  : publicationPending
                    ? isPublished
                      ? "Снимаем..."
                      : "Публикуем..."
                    : isPublished
                      ? "Снять с публикации"
                      : "Опубликовать"}
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] border-rose-300 bg-white px-3.5 text-[13px] font-medium leading-none text-rose-600 transition hover:bg-rose-50 disabled:cursor-wait disabled:opacity-60"
                disabled={deletePending}
                aria-busy={deletePending}
                onClick={deleteListing}
              >
                <Trash2 size={16} />
                {deletePending ? "Удаляем..." : "Удалить"}
              </button>
            ) : null}
          </div>
        </div>
        {shareError ? (
          <p className="m-0 text-right text-xs leading-5 text-[#b42318]" role="alert">
            {shareError}
          </p>
        ) : null}
        {publicationError ? (
          <p className="m-0 text-right text-xs leading-5 text-[#b42318]" role="alert">
            {publicationError}
          </p>
        ) : null}
        {deleteError ? (
          <p className="m-0 text-right text-xs leading-5 text-[#b42318]" role="alert">
            {deleteError}
          </p>
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.75fr)] lg:items-start">
        <div className="grid min-w-0 content-start gap-8">
          <section className="grid gap-2.5" aria-label="Фотографии объявления">
            <div
              className="group relative aspect-[4/3] touch-pan-y select-none overflow-hidden rounded-2xl bg-[#ececec] md:rounded-[22px]"
              onTouchStart={handleImageTouchStart}
              onTouchEnd={handleImageTouchEnd}
              onTouchCancel={() => {
                imageTouchStartRef.current = null;
              }}
            >
              {gallery.length ? (
                gallery.map((image, index) => {
                  const isActive = index === activeImageIndex;

                  return (
                    <img
                      key={image.id}
                      src={image.url}
                      alt={isActive ? listing.title : ""}
                      aria-hidden={!isActive}
                      className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
                        isActive
                          ? "z-[1] scale-100 opacity-100 group-hover:scale-[1.015]"
                          : "pointer-events-none z-0 scale-[1.01] opacity-0"
                      }`}
                    />
                  );
                })
              ) : (
                <div className="grid h-full place-items-center text-sm text-[#8a8a8a]">Нет фотографий</div>
              )}
              {gallery.length > 1 ? (
                <>
                  <button
                    type="button"
                    aria-label="Предыдущее фото"
                    className="absolute left-3 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-white/90 text-[#1a1a1a] shadow-sm transition hover:bg-white"
                    onClick={() => shiftImage(-1)}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    aria-label="Следующее фото"
                    className="absolute right-3 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-white/90 text-[#1a1a1a] shadow-sm transition hover:bg-white"
                    onClick={() => shiftImage(1)}
                  >
                    <ChevronRight size={20} />
                  </button>
                  <span className="absolute bottom-3 right-3 z-10 rounded-full bg-black/65 px-3 py-1 text-xs font-medium text-white">
                    {activeImageIndex + 1} / {gallery.length}
                  </span>
                </>
              ) : null}
            </div>

            {gallery.length > 1 ? (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {gallery.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    aria-label={`Открыть фото ${index + 1}`}
                    className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 bg-[#ececec] transition ${
                      index === activeImageIndex ? "border-[#c8508a]" : "border-transparent opacity-75 hover:opacity-100"
                    }`}
                    onClick={() => setActiveImageIndex(index)}
                  >
                    <img src={image.url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <ListingDescription
            className="hidden border-t border-[#e8e8e8] pt-7 lg:block"
            description={listing.description}
          />
        </div>

        <aside className="grid min-w-0 gap-5 lg:sticky lg:top-6">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-[#fbeaf0] px-3 py-1.5 text-xs font-semibold text-[#8b2a5e]">
              {listing.market_type === "rent" ? "Аренда" : "Продажа"}
            </span>
            <span className="rounded-full bg-[#f2f2f2] px-3 py-1.5 text-xs font-medium text-[#5f5f5f]">
              {CATEGORY_LABELS[listing.board_subcategory || ""] ||
                CATEGORY_LABELS[listing.board_category] ||
                TYPE_LABELS[listing.type]}
            </span>
            {listing.moderation_status && listing.moderation_status !== "approved" ? (
              <span
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  listing.moderation_status === "rejected"
                    ? "bg-[#fef2f2] text-[#b42318]"
                    : "bg-[#fff7ed] text-[#9a3412]"
                }`}
              >
                {MODERATION_LABELS[listing.moderation_status]}
              </span>
            ) : null}
          </div>

          <div>
            <h1 className="m-0 [overflow-wrap:anywhere] text-[30px] font-bold leading-[1.12] tracking-[-0.035em] text-[#1a1a1a] md:text-[38px]">
              {listing.title}
            </h1>
            {addressParts.length ? (
              <p className="mt-3 flex items-start gap-2 text-sm leading-6 text-[#6b6b6b]">
                <MapPin className="mt-0.5 shrink-0 text-[#c8508a]" size={17} />
                {address}
              </p>
            ) : null}
          </div>

          {listing.moderation_status && listing.moderation_status !== "approved" ? (
            <div
              className={`rounded-xl border px-3.5 py-3 text-sm leading-6 ${
                listing.moderation_status === "rejected"
                  ? "border-[#f5c2c7] bg-[#fff5f5] text-[#9f1239]"
                  : "border-[#fed7aa] bg-[#fff7ed] text-[#9a3412]"
              }`}
            >
              <strong className="font-semibold">{MODERATION_LABELS[listing.moderation_status]}.</strong>{" "}
              {listing.moderation_status === "rejected"
                ? listing.moderation_reason || "Причина отказа не указана."
                : "После проверки администратором объявление появится в маркете."}
            </div>
          ) : null}

          <div>
            <div className="text-[30px] font-bold leading-none text-[#8b2a5e]">{primaryPrice || "Договорная"}</div>
            {listing.market_type === "rent" && primaryPrice ? (
              <div className="mt-1.5 text-sm text-[#6b6b6b]">в месяц</div>
            ) : sqmPrice ? (
              <div className="mt-1.5 text-sm text-[#6b6b6b]">
                {formatMoney(unitPrice)} за {unitPriceLabel}
              </div>
            ) : null}
            {rentSummary.length ? (
              <div className="mt-3 grid gap-1 text-sm leading-5 text-[#5f5f5f]">
                {rentSummary.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>
            ) : null}
          </div>

          {facts.length ? (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4 border-y border-[#e8e8e8] py-5">
              {facts.map(({ icon: Icon, label, value }) => (
                <div key={label} className="grid grid-cols-[22px_1fr] gap-x-2">
                  <Icon size={18} strokeWidth={1.8} />
                  <div>
                    <dt className="text-xs text-[#8a8a8a]">{label}</dt>
                    <dd className="m-0 mt-0.5 text-sm font-semibold text-[#1a1a1a]">{value}</dd>
                  </div>
                </div>
              ))}
            </dl>
          ) : null}

          {authorId ? (
            <div className="overflow-hidden rounded-xl border border-[#e8e8e8] bg-white">
              <button
                type="button"
                className="grid w-full grid-cols-[48px_minmax(0,1fr)] items-center gap-3 p-3 text-left transition hover:bg-[#fff8fb] sm:grid-cols-[48px_minmax(0,1fr)_auto]"
                onClick={() =>
                  navigate(`/classifieds/users/${authorId}`, {
                    state: {
                      listingReturnTo: `${location.pathname}${location.search}${location.hash}`,
                    },
                  })
                }
              >
                {authorAvatar ? (
                  <img className="h-12 w-12 rounded-full object-cover" src={authorAvatar} alt={authorName} />
                ) : (
                  <DefaultAvatar name={authorName} color={author?.avatarColor} height={48} width={48} />
                )}
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-[#8a8a8a]">
                    {authorPending
                      ? "Загружаем профиль"
                      : isSharedListing
                        ? "Поделился объявлением"
                        : "Автор объявления"}
                  </span>
                  <span className="mt-0.5 block truncate text-sm font-semibold text-[#1a1a1a]">{authorName}</span>
                </span>
                <span className="col-span-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#8b2a5e] sm:col-span-1">
                  Перейти в профиль
                  <UserRound size={17} />
                </span>
              </button>

              {authorPhoneHref || authorSocials.length ? (
                <div className="flex gap-2 border-t border-[#eeeeee] p-3">
                  {authorPhoneHref ? (
                    <a
                      className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[#c8508a] px-4 text-sm font-semibold text-white transition hover:bg-[#b3407a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c8508a]"
                      href={authorPhoneHref}
                      onClick={() => trackContactClick("phone")}
                    >
                      <Phone size={17} />
                      Позвонить
                    </a>
                  ) : null}
                  {authorSocials.map(({ type: socialType, value, label, Icon }) => (
                    <a
                      key={socialType}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-[#e2d3dc] bg-white transition hover:border-[#c8508a] hover:bg-[#fff8fb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c8508a]"
                      href={getSocialUrl(socialType, value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Написать автору в ${label}`}
                      title={label}
                      onClick={() => trackContactClick(socialType)}
                    >
                      <Icon width={28} height={28} aria-hidden="true" />
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="text-xs text-[#9a9a9a]">Опубликовано {createdAtLabel}</div>
          {showUpdatedAt ? (
            <div className="text-xs text-[#9a9a9a]">Изменено {updatedAtLabel}</div>
          ) : null}
        </aside>
      </div>

      <div className="grid gap-8 border-t border-[#e8e8e8] pt-7 lg:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.7fr)]">
        <div className="grid min-w-0 content-start gap-8">
          <ListingDescription className="lg:hidden" description={listing.description} />

          {addonEntries.length ? (
            <section>
              <h2 className="m-0 text-xl font-bold text-[#1a1a1a]">Характеристики</h2>
              <dl className="mt-3 divide-y divide-[#ececec]">
                {addonEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-[minmax(150px,1fr)_minmax(120px,1fr)] gap-4 py-3 text-sm">
                    <dt className="text-[#777]">{ADDON_LABELS[key] || key}</dt>
                    <dd className="m-0 text-right font-medium text-[#1a1a1a]">{formatAddonValue(key, value)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}
        </div>

        <div className="grid content-start gap-7">
          {mapCoordinates ? (
            <ClassifiedLocationMap
              latitude={mapCoordinates.latitude}
              longitude={mapCoordinates.longitude}
              title={listing.title}
              address={address}
            />
          ) : null}

          {listing.assignment ? (
            <section>
              <h2 className="m-0 text-xl font-bold text-[#1a1a1a]">Переуступка</h2>
              <dl className="mt-3 divide-y divide-[#ececec]">
                {Object.entries(listing.assignment)
                  .filter(([key]) => !ASSIGNMENT_HIDDEN_FIELDS.has(key))
                  .map(([key, value]) => (
                    <div key={key} className="grid grid-cols-[1fr_auto] gap-4 py-3 text-sm">
                      <dt className="text-[#777]">{ASSIGNMENT_LABELS[key] || key}</dt>
                      <dd className="m-0 text-right font-medium text-[#1a1a1a]">
                        {formatAssignmentValue(key, value)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
};

const mapState = (state: RootState) => ({
  user: selectUserInfo(state),
  userData: selectUserData(state),
  userToken: selectUserToken(state),
});

export const ClassifiedListingDetail = connect(mapState)(_ClassifiedListingDetail);
